import { useCallback } from "react";
import { Formik, FormikProps } from "formik";
import { Row, Col } from "antd";
import { Form, SubmitButton, InputNumber } from "formik-antd";
import { useTranslation } from "react-i18next";
import { Integration } from "/app/src/models";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { IconToolTip } from "/app/src/components/icons/IconBuilder";
import useSetting from "/app/src/components/settings/setting";

interface FormValues {
  pollingFrequency: number;
}

/**
 * PollingFrequency component allows users to configure the polling frequency
 * for a specific integration. It provides a form with validation to update
 * the polling frequency setting.
 *
 * @param {Object} props - The component props.
 * @param {Integration} props.integration - The integration object containing
 * the integration details.
 *
 * @returns {JSX.Element} The rendered PollingFrequency component.
 *
 * @remarks
 * - The component uses `Formik` for form handling and validation.
 * - The `useSetting` hook is used to fetch and update the polling frequency setting.
 * - The form includes an input for the polling frequency value and a submit button.
 * - Validation ensures that the polling frequency is a required number.
 *
 * @example
 * ```tsx
 * <PollingFrequency integration={integration} />
 * ```
 */
export default function PollingFrequency({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();
  const {
    settingValue: pollingFrequency,
    createUpdateSetting: createUpdatePollingFrequency,
  } = useSetting({
    integrationId: integration.id,
    type: "pollingFrequency",
  });

  /**
   * Handles the form submission to update the polling frequency.
   *
   * @param {FormValues} values - The form values containing the polling frequency.
   * @returns {Promise<void>} A promise that resolves when the polling frequency is updated.
   */
  const handleSubmit = useCallback(
    async (values: FormValues) => {
      return await createUpdatePollingFrequency({
        value: values.pollingFrequency,
        integrationId: integration.id,
        type: "pollingFrequency",
      });
    },
    [createUpdatePollingFrequency, integration.id],
  );

  const settingsForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ dirty }) => (
        <Form layout="vertical">
          <div className="flex">
            <h3>{t("translation:polling_frequency")}</h3>
            <IconToolTip
              content={t(
                "translation:integration_polling_frequency_description",
              )}
              className="ml-1"
            />
          </div>
          <Row gutter={24}>
            <Col span={20}>
              <Form.Item name="pollingFrequency">
                <InputNumber
                  name="pollingFrequency"
                  size="large"
                  min={10}
                  max={600}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <SubmitButton type="primary" size="large" block disabled={!dirty}>
                {t("translation:save")}
              </SubmitButton>
            </Col>
          </Row>
        </Form>
      ),
      [t],
    );
  return (
    <div className="appSettings">
      <Formik
        component={settingsForm}
        initialValues={{
          pollingFrequency: pollingFrequency
            ? Number(pollingFrequency)
            : undefined,
        }}
        enableReinitialize
        validationSchema={simpleSchemaBuilder([
          { name: "pollingFrequency", type: "number", required: true },
        ])}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
